<template>
  <div class="project-container-wrapper">
    <div class="project-container clearfix">
      <h4>
        <HKLLeftRight>
          <template #left>
            <span>{{ $t('project.tableTitle') }}</span>
            <i
              class="fa fa-refresh dark flat icon_refresh"
              @click="() => loadProjectTable()"
            />
          </template>
          <template
            v-if="isHKlandUser"
            #right
          >
            <HKLButton
              class="btn-reset"
              action="resetFilter"
              color="darkblue"
              @click="() => resetFilterObject()"
            />
          </template>
        </HKLLeftRight>
      </h4>
      <div class="project-table">
        <template>
          <table class="hover">
            <thead>
              <tr>
                <th
                  v-for="(header, index) in itemHeaders"
                  :key="index"
                  :class="header.class"
                  @click="() => sortBy(header.code, header.rank)"
                >
                  {{ header.name }} {{ orderbyAttr === header.code ? reverse ? '&#9660;' : '&#9650;' : null }}
                </th>
              </tr>
              <tr v-if="isHKlandUser">
                <th
                  v-for="header in itemHeaders"
                  :key="header.code"
                >
                  <component
                    :is="header.filterComponent"
                    v-model="filterObject[header.code]"
                    v-bind="header.filterComponentAttrs || {}"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in orderedItems"
                :key="index"
              >
                <!-- @TODO Gino update ui -->
                <td>
                  <div>
                    <a
                      class="link_text"
                      :class="{'bold_text': item.isPending, 'text_red': item.highlightNumber}"
                      @click="() => navToDetailPG(item)"
                    >
                      {{ item.number }}
                    </a>
                  </div>
                </td>
                <td>
                  <a
                    class="link_text"
                    :class="{'bold_text': item.isPending}"
                    @click="() => navToDetailPG(item)"
                  >
                    {{ item.description }}
                  </a>
                  <div
                    v-if="item.showDocIcon"
                    class="new-doc"
                  >
                    {{ $t('tenantProjects.docs') }}
                  </div>
                </td>
                <td
                  :class="{'bold_text': item.isPending}"
                  class="number_text"
                >
                  <span>{{ item.status }}</span>
                  <div
                    v-if="item.isPending"
                    class="orange_bubble"
                  >
                    <i
                      class="fa fa-thumb-tack"
                      aria-hidden="true"
                    />
                  </div>
                </td>
                <td :class="{'bold_text': item.isPending}">
                  {{ item.completion }}
                </td>
                <td
                  class="number_text"
                  :class="{'bold_text': item.isPending}"
                >
                  {{ item.vendor }}
                </td>
                <td
                  class="number_text"
                  :class="{'bold_text': item.isPending}"
                >
                  {{ item.amount }}
                </td>
                <td
                  class="number_text currency_text"
                  :class="{'bold_text': item.isPending}"
                >
                  {{ item.currencyCode }}
                </td>
                <td
                  class="number_text"
                  :class="{'bold_text': item.isPending}"
                >
                  {{ item.managedBy === currentUser.id ? $t('yes') : $t('no') }}
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
      <div class="nav_line">
        <CreateProject
          v-if="showCreateButton()"
          class="nav_button"
        />
        <button
          type="button"
          class="nav_button btn btn--darkblue"
          @click="() => navToAllProjects()"
        >{{ $t('action.viewAllProjects') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { getCurrentInstance, computed, unref, onMounted, ref } from 'vue-demi'
import CurrencyFormatter from 'currency-formatter'
import CreateProject from '@/modules/projects/CreateProject'
import i18n from '@/utils/i18n'
import ordinalUtils from '@/utils/ordinalUtils'
import nav from '@/utils/navigation'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import { getProjects } from '@/api/projects/request'
import { getPurchase, getPurchaseNumber } from '@/api/projects/util'
import { projectStatus, submissionStatus, folderStatus } from '@/utils/locale'
import { showDocIcon as showDocIconFn } from '@/utils/projectUtil'
import useRegionRef from '@/utils/useRegionRef'
import HKLInputOperatorNumber from '@/controls/Input/HKLInputOperatorNumber.vue'
import numberUtils from '@/utils/numberUtils'
import { useStoreGetters } from '@/store/index'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import HKLInput from '@/controls/Input/HKLInput.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLButton from '@/controls/Button/HKLButton.vue'
import { filterValueFn } from '../utils'

const cachedFilterObject = useRegionRef({}, { logoutReset: true })

export default {
  name: 'ProjectsTable',
  components: {
    CreateProject,
    HKLLeftRight,
    HKLButton,
  },
  setup (_, { emit }) {
    const { proxy: that } = getCurrentInstance()
    const { po: poCurrencies, pc: pcCurrencies } = useStoreGetters('currencies', ['po', 'pc'])
    const emptyValue = i18n.t('emptyValue')
    const itemHeaders = computed(() => [
      {
        code: 'number',
        name: `${i18n.t('project.type.po')}/${i18n.t('project.type.contract')}`,
      },
      {
        code: 'poName',
        name: i18n.t('name'),
        class: 'name-header',
        rank: (item) => {
          const purchase = getPurchase(item)
          return lodash.get(purchase, `description`)
        },
        filterNoCase: true,
      },
      {
        code: 'status',
        name: i18n.t('project.submission.AcceptedSubmission'),
        class: 'number_text status-header',
        rank: item => that.getStatus(item)[1],
        onFilterFn: (_, filterValue, __, item) => {
          const [value] = that.getStatus(item)
          return filterValueFn(value, filterValue, { noCase: true })
        },
      },
      {
        code: 'completion',
        name: i18n.t('completion'),
        class: 'completion-header',
        rank: (item) => {
          return that.rankCompletion(item)
        },
        filterComponent: HKLInputOperatorNumber,
        onFilterFn: (value, filterValue) => {
          if (Number(value) === Number.MIN_SAFE_INTEGER) return false
          return numberUtils.compareNumberFree(value, filterValue)
        },
      },
      {
        code: 'vendor',
        name: i18n.t('common.vendor'),
        class: 'number_text vendor-header',
        rank: (item) => {
          const purchase = getPurchase(item)
          return (lodash.get(purchase, 'company.name') || '').toLowerCase()
        },
        filterNoCase: true,
      },
      {
        code: 'poValue',
        name: i18n.t('poContractValue'),
        class: 'number_text amount-header',
        rank: (item) => {
          const purchase = getPurchase(item)
          return purchase.amount
        },
        filterComponent: HKLInputOperatorNumber,
        onFilterFn: (value, filterValue) => {
          return numberUtils.compareNumberFree(value, filterValue)
        },
      },
      {
        code: 'currencyCode',
        name: i18n.t('currency'),
        class: 'number_text currency-header',
        rank: (item) => {
          const purchase = getPurchase(item)
          return purchase.currencyCode
        },
        filterComponent: HKLMultiSelect,
        filterComponentDefaultValue: [],
        filterComponentAttrs: {
          showSearch: true,
          options: lodash.uniq([...unref(poCurrencies), ...unref(pcCurrencies)].filter(Boolean)).sort(),
        },
      },
      {
        code: 'managedBy',
        name: i18n.t('table.header.pic'),
        class: 'number_text pic_text',
        rank: (item) => {
          return item.managedBy === unref(currentUser).id
        },
        filterComponent: HKLSelect,
        // '' = no filter; [] was truthy in init and HKLSelect could coerce to false, hiding all PIC rows.
        filterComponentDefaultValue: '',
        filterComponentAttrs: {
          showSearch: true,
          options: [
            { name: i18n.t('yes'), value: true },
            { name: i18n.t('no'), value: false },
          ],
        },
      },
    ].map((item) => Object.assign({ filterComponent: HKLInput, rank: (val) => val[item.code] }, item)))
    const initFilterObject = () => {
      return unref(itemHeaders).reduce((obj, { code, filterComponentDefaultValue }) => {
        obj[code] = filterComponentDefaultValue !== undefined ? filterComponentDefaultValue : ''
        return obj
      }, {})
    }
    onMounted(() => {
      if (Object.keys(cachedFilterObject.value).length > 0) return
      cachedFilterObject.value = initFilterObject()
    })

    const orderbyAttr = ref(null)
    const orderRank = ref(null)
    const reverse = ref(1)

    const items = ref([])
    const loadProjectTable = async () => {
      const reqPayload = {
        '~status': [
          projectStatus.ACTIVE,
          projectStatus.DRAFT,
        ].join(','),
        managedBy: unref(currentUser).id,
      }
      const allProjects = await getProjects(reqPayload)
      items.value = allProjects
    }
    const isPendingActions = (item) => {
      const srs = lodash.orderBy(item.submissions, 'updatedAt', 'desc')
      if (srs[0] && srs[0].status === submissionStatus.OPEN) {
        return true
      }
      return false
    }
    const parsedItems = computed(() => {
      const result = unref(items).map(item => {
        const purchase = getPurchase(item)
        const amount = lodash.get(purchase, 'amount')
        const hasAmount = amount !== undefined && amount !== null && amount !== ''
        const completionNumber = projectStatus.DRAFT === item.status ? null : that.getCompletionPercentage(item)[0]
        const completion = lodash.isNumber(completionNumber) ? `${completionNumber.toFixed(2)}%` : emptyValue
        return {
          ...item,
          number: getPurchaseNumber(item.numberType, purchase) || emptyValue,
          showDocIcon: showDocIconFn(item),
          description: lodash.get(purchase, 'description') || emptyValue,
          status: that.getStatus(item)[0],
          completionNumber,
          completion,
          vendor: lodash.get(purchase, 'company.name') || emptyValue,
          amount: hasAmount ? CurrencyFormatter.format(amount, 'USD') : emptyValue,
          currencyCode: lodash.get(purchase, 'currencyCode') || emptyValue,
          isPending: isPendingActions(item),
        }
      }).filter(item => {
        return item.completionNumber !== 100
      })
      emit('update:items', unref(result))
      return result
    })
    const filteredItems = computed(() => {
      return unref(parsedItems).filter((item, index) => {
        return unref(itemHeaders).every(({ code, onFilterFn, rank, filterNoCase = false }) => {
          const filterValue = unref(cachedFilterObject)[code]
          const value = rank(item)
          const result = filterValueFn(value, filterValue, {
            noCase: filterNoCase,
            customDiffFn: typeof onFilterFn === 'function' ? (a, b) => onFilterFn(a, b, index, item) : undefined,
          })
          return result
        })
      })
    })
    const orderedItems = computed(() => {
      const pendingActionResult = that.sortedItems(
        unref(filteredItems).filter(isPendingActions),
      )
      const notPendingResult = that.sortedItems(
        unref(filteredItems).filter(item => !isPendingActions(item)),
      )
      return [pendingActionResult, notPendingResult]
        .map(list => unref(reverse) ? list.reverse() : list)
        .flat()
        .map((project) => ({
          ...project,
          highlightNumber: project.projectType === 'ProjectRep',
        }))
    })
    const { isContractor, currentUser, isHKlandUser } = useStoreGetters('user', ['isContractor', 'currentUser', 'isHKlandUser'])
    onMounted(async () => {
      await loadProjectTable()
    })
    return {
      isContractor,
      currentUser,
      isHKlandUser,
      orderbyAttr,
      orderRank,
      reverse,
      items,
      parsedItems,
      filteredItems,
      orderedItems,
      itemHeaders,
      filterObject: cachedFilterObject,
      resetFilterObject () {
        cachedFilterObject.value = initFilterObject()
        orderbyAttr.value = null
        orderRank.value = null
        reverse.value = 1
      },
      projectStatus,
      currencyFormatter: CurrencyFormatter,
      loadProjectTable,
      isPendingActions,
    }
  },
  methods: {
    navToDetailPG (item) {
      nav.toPage(this.isContractor ? 'vendorProjectDetail' : 'projectDetail', { id: item.id })
    },
    navToAllProjects () {
      nav.toPage('projects')
    },
    rankCompletion (item) {
      const percentage = this.getCompletionPercentage(item)
      return (projectStatus.DRAFT === item.status) ? Number.MIN_SAFE_INTEGER : percentage[0]
    },
    getStatus (item) {
      const srs = lodash.orderBy(item.submissions, 'createdAt', 'desc')
      const srCount = srs.length
      if (item.status === projectStatus.ACTIVE) {
        if (srCount > 0 && srs[0].status === submissionStatus.APPR) {
          return [ordinalUtils.getOrdinalFor(srCount, true), srCount]
        } else if (srCount > 1) {
          if (srs[1].status === submissionStatus.DRAFT) {
            return [(srCount === 2) ? i18n.t('project.nil') : ordinalUtils.getOrdinalFor(srCount - 2, true), srCount - 2]
          }
          return [ordinalUtils.getOrdinalFor(srCount - 1, true), srCount - 1]
        } else {
          return [i18n.t('project.nil'), 0]
        }
      }
      return [item.status, -1]
    },
    getCompletionPercentage (item) {
      let completedAmountPercent = 0
      let totalAmount = 0
      const purchase = getPurchase(item)
      if (purchase && purchase.amount > 0) {
        completedAmountPercent = (item.completedAmount * 100)
        totalAmount = (((lodash.get(purchase, 'amount') || 0) - (lodash.get(item, 'deduction.amount') || 0)))
      }
      return [totalAmount ? (completedAmountPercent / totalAmount).toFixed(2) - 0 : 0, completedAmountPercent, totalAmount]
    },
    // @TODO gino change to document level
    getDocumentsAccepted (item) {
      const approvedDocs = (item.folders || []).filter(folder => folder.status === folderStatus.APPR).length
      const totalDocs = item.folders.length
      return [`${approvedDocs} / ${totalDocs}`, approvedDocs, totalDocs]
    },
    sortBy (attr, rank) {
      if (this.orderbyAttr === attr) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attr
      this.orderRank = rank
    },
    showCreateButton () {
      return aclHelper.getAccess(aclPermsType.projects.manage)
    },
    sortedItems (items) {
      if (this.orderbyAttr) {
        return lodash.orderBy(items, this.orderRank || this.orderbyAttr)
      } else {
        return lodash.orderBy(items, [this.isPendingActions, 'showDocIcon', this.rankCompletion])
      }
    },
  },
  template: 'projects-table',
}
</script>

<style lang="scss" scoped>
@import '~styles/dashboard/datatable.scss';

.project-table {
  border-radius: 4px;
  background: white;
}

td i, td a, td span, td div i {
  vertical-align: middle;
}

.orange_bubble {
  vertical-align: middle;
  position: relative;
  background-color: #e69500;
  border-radius: 7px;
  width: 28px;
  height: 14px;
  display: inline-block;
  text-align: center;
  margin-right: 2px;

  i {
    position: absolute;
    top: 2px;
    left: 10px;
    transform: rotate(45deg);
    font-size: 12px;
    color: white;
  }
}

.new-doc {
  display: inline-block;
  margin-left: 8px;
}

.number-header {
  width: 10%;
}
.name-header {
  width: 39%;
}
.status-header {
  width: 10%
}
.completion-header {
  width: 11%
}
.vendor-header {
  width: 20%
}
.amount-header {
  width: 10%
}

.currency-header,
.currency_text {
  min-width: 125px;
  width: 10%;
}

.pic_text {
  min-width: 90px;
  width: 5%;
}

h4 {
  &:has(.btn-reset) {
    padding-bottom: 8px;
  }
  & > .hkl-left-right {
    align-items: center;
    span + .icon_refresh {
      margin-left: 5px;
    }
    :has(.btn-reset) {
      padding-bottom: 8px;
    }
  }
}
</style>
